/**
 * SystemCommonCodeCreatePage Component
 * 공통코드 추가/수정 페이지
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PrimaryButton from '@/shared/components/buttons/PrimaryButton';
import Loading from '@/shared/components/feedback/Loading';
import { commonCodeApi } from '../api/systemCommonCodeApi';
import { getItem, removeItem } from '@/shared/utils/storage';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import {
    extractCodesFromResponse,
    extractGroupsFromResponse,
    calculateVisibleValueFields,
} from '../../shared/lib/commonCodeParserHelpers';
import {
    SYSTEM_STORAGE_KEYS,
    DASHBOARD_EVENTS,
    SYSTEM_TAB_IDS,
} from '../../shared/config/system.config';
import GroupInfoSection from './GroupInfoSection';
import CommonCodeItemCard from './CommonCodeItemCard';
import {
    CommonCodeFormItem,
    GroupFormData,
    TOAST_MESSAGES,
    createEmptyCodeItem,
    createEmptyGroupFormData,
    getToggledValueFieldState,
    validateForm,
} from './commonCodeCreateHelpers';
import '../styles/SystemCommonCodeCreatePage.css';

// ======== 컴포넌트 ========

const SystemCommonCodeCreatePage: React.FC = () => {
    const { showToast } = useToast();
    const pageHeaderRef = useRef<HTMLDivElement>(null);

    // ======== 상태 ========

    /** 수정 모드 그룹코드 (localStorage에서 읽기) */
    const [grpCode] = useState<string | undefined>(() => {
        const savedGrpCode = getItem<string>(SYSTEM_STORAGE_KEYS.COMMON_CODE_EDIT_GRP_CODE);
        if (savedGrpCode) {
            removeItem(SYSTEM_STORAGE_KEYS.COMMON_CODE_EDIT_GRP_CODE);
            return savedGrpCode;
        }
        removeItem(SYSTEM_STORAGE_KEYS.COMMON_CODE_EDIT_MODE);
        return undefined;
    });

    const [formData, setFormData] = useState<GroupFormData>(createEmptyGroupFormData);
    const [commonCodes, setCommonCodes] = useState<CommonCodeFormItem[]>([createEmptyCodeItem()]);
    const [loading, setLoading] = useState(false);

    const isEditMode = !!grpCode;

    // ======== 초기화 Effects ========

    /**
     * 페이지 마운트 시 스크롤을 상단으로 이동
     */
    useEffect(() => {
        window.scrollTo(0, 0);
        requestAnimationFrame(() => {
            window.scrollTo(0, 0);
            pageHeaderRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
        });
    }, []);

    /**
     * 수정 모드: 그룹 데이터 조회
     */
    useEffect(() => {
        if (!grpCode) return;

        const fetchGroupData = async () => {
            setLoading(true);
            try {
                const response = await commonCodeApi.getCommonCode(grpCode);
                const codes = extractCodesFromResponse(response);
                const groups = extractGroupsFromResponse(response);
                const group = groups.find(g => g.grpCode === grpCode);

                if (group) {
                    setFormData({
                        grpCode: group.grpCode,
                        grpName: group.grpName,
                        grpVal: group.grpVal,
                        seqNo: group.seqNo,
                    });

                    if (codes.length > 0) {
                        const groupCodes = codes.filter(code => code.grpCode === grpCode);
                        setCommonCodes(groupCodes.map((code, index) => ({
                            id: `code-${Date.now()}-${index}`,
                            comCode: code.comCode,
                            comCdName: code.comCdName,
                            comSeqno: (code.comSeqno || code.seqNo)?.toString() || '',
                            seqNo: code.seqNo,
                            useYn: code.useYn,
                            val1: code.val1 || '',
                            val2: code.val2 || '',
                            val3: code.val3 || '',
                            val4: code.val4 || '',
                            val5: code.val5 || '',
                            visibleValueFields: calculateVisibleValueFields(code),
                        })));
                    }
                }
            } catch {
                // 데이터 로드 실패 시 조용히 처리
            } finally {
                setLoading(false);
            }
        };

        fetchGroupData();
    }, [grpCode]);

    /**
     * 수정 모드 데이터 로드 완료 후 스크롤 상단 이동
     */
    useEffect(() => {
        if (!grpCode || commonCodes.length === 0) return;

        const scrollToTop = () => {
            (document.activeElement as HTMLElement)?.blur?.();
            window.scrollTo(0, 0);
            pageHeaderRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' });
        };

        [0, 100, 200].forEach(delay => setTimeout(scrollToTop, delay));
    }, [grpCode, commonCodes.length]);

    // ======== 그룹 핸들러 ========

    /**
     * 그룹코드 필드 변경
     */
    const handleGroupChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    }, []);

    // ======== 공통코드 핸들러 ========

    /**
     * 공통코드 필드 변경
     */
    const handleCommonCodeChange = useCallback((
        id: string,
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setCommonCodes(prev => prev.map(code =>
            code.id === id ? { ...code, [name]: value } : code
        ));
    }, []);

    /**
     * 공통코드 추가
     */
    const handleAddCommonCode = useCallback(() => {
        setCommonCodes(prev => [...prev, createEmptyCodeItem()]);
    }, []);

    /**
     * 공통코드 삭제
     */
    const handleRemoveCommonCode = useCallback((id: string) => {
        setCommonCodes(prev => {
            if (prev.length === 1) {
                showToast(TOAST_MESSAGES.MIN_CODE_REQUIRED, { type: ToastType.WARNING });
                return prev;
            }
            return prev.filter(code => code.id !== id);
        });
    }, [showToast]);

    /**
     * 코드값 필드 추가/삭제
     */
    const handleToggleValueField = useCallback((id: string, fieldIndex: number) => {
        setCommonCodes(prev => prev.map(code => {
            if (code.id !== id) return code;
            return { ...code, ...getToggledValueFieldState(code, fieldIndex) };
        }));
    }, []);

    // ======== 폼 제출 ========

    /**
     * 폼 제출 핸들러
     */
    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        const errorMessage = validateForm(formData, commonCodes);
        if (errorMessage) {
            showToast(errorMessage, { type: ToastType.ERROR });
            return;
        }

        const actionText = isEditMode ? '수정' : '등록';
        if (!window.confirm(`공통코드를 ${actionText}하시겠습니까?`)) return;

        setLoading(true);
        try {
            const userId = getItem<string>('USER_ID');
            if (!userId) {
                showToast(TOAST_MESSAGES.LOGIN_NOT_FOUND, { type: ToastType.ERROR });
                return;
            }

            // 1. 그룹코드 저장
            const groupResponse = await commonCodeApi.saveCommonCodeGroup({
                userId,
                grpCode: formData.grpCode,
                grpName: formData.grpName,
                grpVal: formData.grpVal || undefined,
                seqNo: formData.seqNo,
            });

            if (!groupResponse.success) {
                showToast(groupResponse.message || TOAST_MESSAGES.GROUP_SAVE_FAIL, { type: ToastType.ERROR });
                return;
            }

            // 2. 공통코드 저장 (병렬)
            const codeResponses = await Promise.all(
                commonCodes.map(code => commonCodeApi.saveCommonCode({
                    userId,
                    grpCode: formData.grpCode,
                    comCode: code.comCode,
                    comCdName: code.comCdName,
                    comSeqno: code.comSeqno.trim() || undefined,
                    seqNo: code.seqNo,
                    useYn: code.useYn,
                    val1: code.val1.trim() || undefined,
                    val2: code.val2.trim() || undefined,
                    val3: code.val3.trim() || undefined,
                    val4: code.val4.trim() || undefined,
                    val5: code.val5.trim() || undefined,
                }))
            );

            const allSuccess = codeResponses.every(res => res.success);
            
            // 그룹코드와 공통코드 모두 변경사항이 없는 경우에만 경고
            const groupHasChanges = groupResponse.hasChanges !== false;
            const codeHasChanges = codeResponses.some(res => res.hasChanges !== false);
            
            if (!groupHasChanges && !codeHasChanges) {
                showToast(TOAST_MESSAGES.NO_CHANGES, { type: ToastType.WARNING });
                return;
            }

            if (allSuccess) {
                showToast(TOAST_MESSAGES.SAVE_SUCCESS(isEditMode), { type: ToastType.SUCCESS });
                window.dispatchEvent(new CustomEvent(DASHBOARD_EVENTS.COMMON_CODE_REFRESH));
                window.dispatchEvent(new CustomEvent(DASHBOARD_EVENTS.NAVIGATE, {
                    detail: { page: SYSTEM_TAB_IDS.COMMON_CODE }
                }));
            } else {
                const failedRes = codeResponses.find(res => !res.success);
                showToast(failedRes?.message || TOAST_MESSAGES.SAVE_FAIL(isEditMode), { type: ToastType.ERROR });
            }
        } catch {
            showToast(TOAST_MESSAGES.SAVE_FAIL(isEditMode), { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [formData, commonCodes, isEditMode, showToast]);

    /**
     * 취소 핸들러
     */
    const handleCancel = useCallback(() => {
        window.dispatchEvent(new CustomEvent(DASHBOARD_EVENTS.CLOSE_TAB, {
            detail: { tabId: SYSTEM_TAB_IDS.COMMON_CODE_ADD }
        }));
        window.dispatchEvent(new CustomEvent(DASHBOARD_EVENTS.NAVIGATE, {
            detail: { page: SYSTEM_TAB_IDS.COMMON_CODE }
        }));
    }, []);

    // ======== 렌더링 ========

    return (
        <div className="common-code-create-page">
            {/* 페이지 헤더 */}
            <div className="page-header" ref={pageHeaderRef}>
                <div>
                    <h1 className="page-title">
                        {isEditMode ? '공통코드 수정' : '공통코드 추가'}
                    </h1>
                    <p className="page-subtitle">
                        {isEditMode ? '기존 공통코드 정보를 수정하세요' : '새로운 공통코드를 등록하세요'}
                    </p>
                </div>
            </div>

            {/* 로딩 */}
            {loading && (
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <Loading message="그룹 데이터를 불러오는 중..." />
                </div>
            )}

            {/* 폼 */}
            <div className="form-container">
                <form onSubmit={handleSubmit} className="common-code-form" noValidate autoComplete="off">
                    {/* 그룹코드 정보 섹션 */}
                    <GroupInfoSection formData={formData} onChange={handleGroupChange} />

                    {/* 공통코드 정보 섹션 */}
                    <div className="form-section">
                        <div className="form-section-header">
                            <h2 className="form-section-title">공통코드 정보</h2>
                            {!isEditMode && (
                                <PrimaryButton
                                    type="button"
                                    onClick={handleAddCommonCode}
                                    showArrow={false}
                                    className="add-common-code-button"
                                >
                                    <i className="fas fa-plus"></i>
                                    공통코드 추가
                                </PrimaryButton>
                            )}
                        </div>

                        {commonCodes.map((code, index) => (
                            <CommonCodeItemCard
                                key={code.id}
                                code={code}
                                index={index}
                                canDelete={commonCodes.length > 1}
                                onChange={handleCommonCodeChange}
                                onRemove={handleRemoveCommonCode}
                                onToggleValueField={handleToggleValueField}
                            />
                        ))}
                    </div>

                    {/* 액션 버튼 */}
                    <div className="form-actions">
                        <PrimaryButton
                            type="button"
                            onClick={handleCancel}
                            showArrow={false}
                            className="cancel-button"
                        >
                            취소
                        </PrimaryButton>
                        <PrimaryButton
                            type="submit"
                            showArrow={false}
                            className="submit-button"
                            disabled={loading}
                            loading={loading}
                        >
                            {isEditMode ? '수정' : '등록'}
                        </PrimaryButton>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SystemCommonCodeCreatePage;
